/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from 'antd';
import styles from './styles.module.css';
import domestic from '/public/images/domestic.png';
import religious from '/public/images/religious.png';
import industrial from '/public/images/industrial.png';
import hotel from '/public/images/hotel.png';
import government from '/public/images/government.png';
import streetLighting from '/public/images/street_lighting.png';
import lecoEvCharging from '/public/images/leco_ev_charging.png';
import evCharging from '/public/images/ev_charging.png';
import agriculture from '/public/images/agriculture.png';
import generalPurpose from '/public/images/general_purpose.png';

import { withConfigContext } from '../../components/ConfigContext';
import { withTranslation } from 'react-i18next';
import PageLoading from '../../components/PageLoading';
import TariffBreakdownNavBar from './components/TariffBreakdownNavBar';
import TariffBreakdownSidebar from './components/TariffBreakdownSidebar';
import { URLParamsContext } from '../../index';

export const RoutesContext = React.createContext();

const TariffBreakdown = ({ context, t }) => {
  const [selectedCategory, setSelectedCategory] = useState('Domestic');
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
  const isMobile = window.innerWidth < 980;

  const toggleMobileMenu = () => {
    setIsMobileMenuVisible(!isMobileMenuVisible);
  };

  const categoryIcons = {
    Domestic: (
      <img src={domestic} alt="Domestic" className={styles.categoryIcon} />
    ),
    Religious: (
      <img src={religious} alt="Religious" className={styles.categoryIcon} />
    ),
    Industrial: (
      <img src={industrial} alt="Industrial" className={styles.categoryIcon} />
    ),
    Hotel: <img src={hotel} alt="Hotel" className={styles.categoryIcon} />,
    Government: (
      <img src={government} alt="Government" className={styles.categoryIcon} />
    ),
    Agriculture: (
      <img
        src={agriculture}
        alt="Agriculture"
        className={styles.categoryIcon}
      />
    ),
    'General Purpose': (
      <img
        src={generalPurpose}
        alt="General Purpose"
        className={styles.categoryIcon}
      />
    ),
    'Street Lighting': (
      <img
        src={streetLighting}
        alt="Street Lighting"
        className={styles.categoryIcon}
      />
    ),
    'LECO EV Charging Stations': (
      <img
        src={lecoEvCharging}
        alt="LECO EV Charging Stations"
        className={styles.categoryIcon}
      />
    ),
    'EV Charging Stations': (
      <img
        src={evCharging}
        alt="EV Charging Stations"
        className={styles.categoryIcon}
      />
    ),
  };

  const categories = [
    'Domestic',
    'Religious',
    'Industrial',
    'Hotel',
    'Government',
    'Agriculture',
    'General Purpose',
    'Street Lighting',
    'LECO EV Charging Stations',
    'EV Charging Stations',
  ];

  const [tariffData, setTariffData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { showNavbar } = React.useContext(URLParamsContext);

  const getTariffBreakdownData = () => {
    setLoading(true);
    setError(null);
    axios
      .get(
        window.location.origin +
          context.serverConfig.invoker.uri +
          '/api/bill-calculation/tariff-breakdown',
      )
      .then((res) => {
        setTariffData(res.data);
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getTariffBreakdownData();
  }, []);

  const quickInfo = [
    'Charges in LKR (Rs)',
    'Energy Chargers: LKR/kWh',
    'Demand Chargers: LKR/kVA',
    'Fixed Chargers: LKR/month',
  ];

  return (
    <>
      <TariffBreakdownNavBar
        context={context}
        t={t}
        tariffData={tariffData}
        isMobile={isMobile}
        isMobileMenuVisible={isMobileMenuVisible}
        toggleMobileMenu={toggleMobileMenu}
        selectedCategory={selectedCategory}
      />

      <div className={styles.tariffBreakdownContainer}>
        {(!isMobile || isMobileMenuVisible) && (
          <>
            {isMobile && isMobileMenuVisible && (
              <div
                className={styles.mobileOverlay}
                onClick={() => setIsMobileMenuVisible(false)}
              />
            )}
            <TariffBreakdownSidebar
              context={context}
              t={t}
              categories={categories}
              categoryIcons={categoryIcons}
              selectedCategory={selectedCategory}
              isMobile={isMobile}
              isMobileMenuVisible={isMobileMenuVisible}
              setSelectedCategory={setSelectedCategory}
              setIsMobileMenuVisible={setIsMobileMenuVisible}
              quickInfo={quickInfo}
              showNavbar={showNavbar}
            />
          </>
        )}

        <div className={styles.mainContent}>
          {loading ? (
            <PageLoading
              message={t('label_loading_tariff_data', 'Loading tariff data...')}
            />
          ) : error ? (
            <div className={styles.errorContainer}>
              <div className={styles.errorIcon}>⚠️</div>
              <h2 className={styles.errorTitle}>
                {t('label_error_loading_data', 'Error Loading Data')}
              </h2>
              <p className={styles.errorMessage}>
                {t(
                  'label_tariff_breakdown_error_message',
                  'Failed to load tariff breakdown data. Please try again.',
                )}
              </p>
              <Button
                type="primary"
                onClick={getTariffBreakdownData}
                className={styles.retryButton}
              >
                {t('label_retry', 'Retry')}
              </Button>
            </div>
          ) : (
            <div className={styles.contentContainer}>
              {showNavbar && (
                <div className={styles.header}>
                  <span className={styles.headerIcon}>
                    {categoryIcons[selectedCategory]}
                  </span>
                  <div>
                    <h1 className={styles.pageTitle}>{selectedCategory}</h1>
                    <h3 className={styles.pageSubTitle}>
                      {t('label_detailed_pricing_breakdown', {
                        label: selectedCategory,
                      })}
                    </h3>
                  </div>
                </div>
              )}
              {(() => {
                const selectedTariff =
                  tariffData &&
                  tariffData?.tariff.find(
                    (t) => t.category === selectedCategory,
                  );
                if (!selectedTariff) {
                  return null;
                }
                return (
                  <div className={styles.tariffSections}>
                    {selectedTariff.slabs.map((slab, slabIndex) => {
                      // Check if all fixed charges are null in this slab
                      const allFixedChargesNull = slab.blocks.every(
                        (block) => block.fixedCharge === null,
                      );
                      // Check if any block has time data
                      const hasTimeData = slab.blocks.some(
                        (block) => block.time,
                      );

                      return (
                        <div key={slabIndex} className={styles.tariffSection}>
                          <h3 className={styles.sectionTitle}>
                            {slab?.monthlyConsumption
                              ?.charAt(0)
                              ?.toUpperCase() +
                              slab?.monthlyConsumption?.slice(1)}
                          </h3>
                          <div className={styles.tableContainer}>
                            <table className={styles.tariffTable}>
                              <thead>
                                <tr>
                                  <th>{t('label_consumption')}</th>
                                  {hasTimeData && <th>{t('label_time')}</th>}
                                  <th className={styles.textRight}>
                                    {t('label_energy_charge')}
                                  </th>
                                  {!allFixedChargesNull && (
                                    <th className={styles.textRight}>
                                      {t('label_fixed_charges')}
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {slab.blocks.map((block, blockIndex) => (
                                  <tr key={blockIndex}>
                                    <td>
                                      {block?.slab?.charAt(0)?.toUpperCase() +
                                        block?.slab?.slice(1)}
                                    </td>
                                    {hasTimeData && (
                                      <td>
                                        {block?.time != null ? block.time : '-'}
                                      </td>
                                    )}
                                    <td className={styles.textRight}>
                                      {block.energyCharge != null
                                        ? block.energyCharge
                                            .toFixed(2)
                                            .replace(
                                              /\B(?=(\d{3})+(?!\d))/g,
                                              ',',
                                            )
                                        : block.demandCharge != null
                                          ? block.demandCharge
                                              .toFixed(2)
                                              .replace(
                                                /\B(?=(\d{3})+(?!\d))/g,
                                                ',',
                                              )
                                          : '-'}
                                    </td>
                                    {!allFixedChargesNull && (
                                      <td className={styles.textRight}>
                                        {block.fixedCharge != null
                                          ? block.fixedCharge
                                              .toFixed(2)
                                              .replace(
                                                /\B(?=(\d{3})+(?!\d))/g,
                                                ',',
                                              )
                                          : '-'}
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

TariffBreakdown.contextType = RoutesContext;
export default withConfigContext(withTranslation()(TariffBreakdown));
